import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import isUndefined from 'lodash.isundefined';
import PropTypes from 'prop-types';

// components
import LoadingArea from '@molecules/LoadingArea';
import { BodyMedium } from '@styled';
import AccountCompanyInformationFields from './AccountCompanyInformationFields';
import AccountContactInformationFields from './AccountContactInformationFields';
import AccountInformationFormHeader from './AccountInformationFormHeader';
import NotificationBanner from '@molecules/NotificationBanner';
import GdprConsent from '@molecules/GdprConsent';
import CaslConsent from '@molecules/CaslConsent';
import ErrorFocus from './ErrorFocus';

// functionality
import { LoadingContext } from '@context/LoadingProvider';
import { addCompany, addCompanyMainAddress } from '@general/api/companyAPI';
import { insertMember, updateMember } from '@general/api/memberAPI';
import { mapAPIMemberToProps } from '@general/transforms/member.transform';
import { updateCartItem } from '@general/api/cartAPI';
import useMemberFromAPI from '@hooks/apiLegacy/useMemberFromAPI';
import useViewer from '@hooks/useViewer';
import useCartFromAPI from '@hooks/apiLegacy/useCartFromAPI';
import { getCommonSchema, newCompanySchema, defaultFormValues, getUserFriendlyErrorMessage } from './index.helpers';
import { CompanyFieldsProvider } from './CompanyFieldsContext';

const AccountInformationForm = ({ disableCompanySelection, formRef, memberId, onMemberSaved, viewerId }) => {
    const { setIsLoading } = useContext(LoadingContext);
    const [memberWorking, setMemberWorking] = useState();
    const [isIndependent, setIsIndependent] = useState(false);
    const [isNewCompany, setIsNewCompany] = useState(false);
    const [isManualCompanyAddress, setIsManualCompanyAddress] = useState(true);
    const [isManualContactAddress, setIsManualContactAddress] = useState(true);
    const [initialCompanyID, setInitialCompanyID] = useState(false);
    const [isPrimaryEmailRequired, setIsPrimaryEmailRequired] = useState(false);
    const [initialFormValues, setInitialFormValues] = useState(defaultFormValues);
    const [ageValidationError, setAgeValidationError] = useState('');
    const [errorMessages, setErrorMessages] = useState([]);
    const { member: memberFromAPI, isLoading, refetch: refetchMember } = useMemberFromAPI({ id: memberId || viewerId });
    const { viewer } = useViewer();
    const { cart } = useCartFromAPI({ viewerId: viewer?.recordId });
    const [isCaslConsentRequired, setIsCaslConsentRequired] = useState(false);
    const [isGdprConsentRequired, setIsGdprConsentRequired] = useState(false);
    const [hasSecondaryBusinessTypes, setHasSecondaryBusinessTypes] = useState(false);

    const isNewMember = memberId === null;

    useEffect(() => {
        if (!isNewMember) {
            if (isUndefined(memberFromAPI)) {
                return;
            }

            setMemberWorking(memberFromAPI);
        }

        if (isNewMember) {
            setMemberWorking(null);
            setIsPrimaryEmailRequired(true);
        }

        if (memberFromAPI) {
            handleDataLoaded(memberFromAPI);
        }
    }, [handleDataLoaded, isNewMember, memberFromAPI]);

    useEffect(() => {
        if (!errorMessages || errorMessages.length === 0) return;
        window.scrollTo(0, 0);
    }, [errorMessages]);

    const handleDataLoaded = useCallback(
        memberData => {
            let combinedValues;

            // Refetch member to grab company name for form on add new membership
            if (memberData.deferred_company?.id && !memberData.deferred_company?.name) {
                refetchMember();
            }

            const mappedAPIMember = mapAPIMemberToProps({ member: memberData, preferDeferredCompany: true });

            const { companyID, companyName, independent } = mappedAPIMember;

            // Load company values from viewer if new member
            if (isNewMember) {
                combinedValues = {
                    ...defaultFormValues,
                    companyID,
                    companyName,
                    independent,
                };
            } else {
                combinedValues = {
                    ...defaultFormValues,
                    ...mappedAPIMember,
                };
            }

            setInitialFormValues(combinedValues);
            setInitialCompanyID(combinedValues.companyID);

            if (combinedValues.independent) {
                setIsIndependent(true);
            }
        },
        [isNewMember, refetchMember]
    );

    const saveMember = async (formValues, setFieldValue) => {
        setIsLoading(true);
        const memberPayload = {
            birthdate: {
                year: parseInt(formValues.birthdateYear),
                month: parseInt(formValues.birthdateMonth),
                day: parseInt(formValues.birthdateDay),
            },
            cell_phone: formValues.cellPhone,
            company: {
                name: formValues.companyName,
                id: formValues.companyID,
            },
            email: formValues.email,
            first_name: formValues.firstName,
            last_name: formValues.lastName,
            main_address: {
                address_1: formValues.mainAddressAddress1,
                address_2: formValues.mainAddressAddress2,
                address_3: formValues.mainAddressAddress3,
                city: formValues.mainAddressCity,
                state_province: formValues.mainAddressState,
                zip: formValues.mainAddressZip,
                country: formValues.mainAddressCountry,
            },
            personal_responsibility: formValues.personalResponsibility,
            secondary_email: formValues.secondaryEmail,
            sms_optin: formValues.smsUpdates,
            title: formValues.title,
            work_phone: formValues.workPhone,
        };

        if (isCaslConsentRequired) {
            memberPayload.casl_express_consent = formValues.caslExpressConsent;
        }

        if (isGdprConsentRequired) {
            memberPayload.gdpr_consent = formValues.gdprConsent;
        }

        let companyHasChanged = initialCompanyID !== formValues.companyID;

        if (isNewMember || (formValues.companyID && companyHasChanged)) {
            memberPayload.deferred_company_id = formValues.companyID;
            memberPayload.deferred_company_name = formValues.companyName;
        }

        if (isIndependent) {
            memberPayload.business_type = 'OBS';
            memberPayload.secondary_business_types = [];
            memberPayload.company_id = null;
            memberPayload.deferred_company_id = null;
            memberPayload.deferred_independent = true;
        } else {
            memberPayload.deferred_independent = false;
        }

        const newCompanyPayload = {
            business_type: formValues.businessType,
            name: formValues.newCompanyName,
            secondary_business_types: formValues.secondaryBusinessTypes,
            minority_owned: formValues.minorityOwned,
            woman_owned: formValues.womanOwned,
            retail_confirm_member_id: formValues.businessType === "RET" ? viewer?.recordId : null,
        };

        const newCompanyAddress = {
            address_1: formValues.companyAddressAddress1,
            address_2: formValues.companyAddressAddress2,
            address_3: formValues.companyAddressAddress3,
            city: formValues.companyAddressCity,
            state_province: formValues.companyAddressState,
            country: formValues.companyAddressCountry,
            zip: formValues.companyAddressZip,
            purpose: 'main',
            preferred_billing: true,
            preferred_mailing: true,
            preferred_shipping: true,
        };

        try {
            let memberResponse;
            let newCompanyResponse;
            let newCompanyId;
            let newCompanyName;

            // Add new company, get company ID, add to memberPayload
            if (isNewCompany && !isIndependent) {
                newCompanyResponse = await addCompany(newCompanyPayload);
                newCompanyId = newCompanyResponse.data?.data?.id;
                newCompanyName = newCompanyResponse.data?.data?.name;
                memberPayload.deferred_company = {};
                memberPayload.deferred_company.id = newCompanyId;
                memberPayload.deferred_company.name = newCompanyName;
                memberPayload.deferred_company_id = newCompanyId;
                memberPayload.deferred_company_name = newCompanyName;
                companyHasChanged = true;
                setFieldValue('companyID', newCompanyId);
                setFieldValue('companyName', newCompanyName);

                // Add main address for new company
                await addCompanyMainAddress(newCompanyId, newCompanyAddress);
            }

            if (!memberId) {
                memberResponse = await insertMember(memberPayload, true);
            } else {
                memberResponse = await updateMember(memberId, memberPayload);

                if (companyHasChanged) {
                    const updatedCompanyName = memberPayload.deferred_independent
                        ? null
                        : memberPayload.deferred_company_name || memberPayload.company?.name || null;

                    // Update meta_info.company_name for all cart items after company change
                    await Promise.all(
                        cart?.items.map(async item => {
                            const updatedItem = {
                                ...item,
                                meta_info: {
                                    ...item.meta_info,
                                    company_name: updatedCompanyName,
                                },
                            };

                            await updateCartItem({
                                cartId: cart.id,
                                cartItemId: updatedItem.id,
                                cartItemData: {
                                    meta_info: updatedItem.meta_info,
                                },
                                viewerId,
                            });
                        })
                    );
                }
            }

            onMemberSaved(memberResponse);
        } catch (error) {
            setErrorMessages(error.response?.data?.errors);
            console.error(error);
            setIsLoading(false);
        } finally {
            // Let the parent handling the closing of the loading overlay
        }
    };

    if (!viewer || isUndefined(memberWorking) || isLoading) {
        return <LoadingArea />;
    }

    const commonSchema = getCommonSchema({
        isCaslConsentRequired,
        isGdprConsentRequired,
        isIndependent,
        isManualContactAddress,
        isNewCompany,
    });
    const validationSchema =
        isNewCompany && !isIndependent
            ? commonSchema.concat(newCompanySchema({ isManualCompanyAddress, hasSecondaryBusinessTypes }))
            : commonSchema;

    return (
        <Formik
            enableReinitialize
            initialValues={initialFormValues}
            innerRef={formRef}
            validationSchema={validationSchema}
            onSubmit={(values, { setFieldValue }) => {
                setErrorMessages([]);

                if (!ageValidationError) {
                    saveMember(values, setFieldValue);
                }
            }}
        >
            {({ setFieldValue, values, setTouched, touched }) => {
                return (
                    <Form>
                        <AccountInformationFormHeader isNew={isNewMember} />

                        {errorMessages && (
                            <div className="mb-1rem mt-1rem">
                                {errorMessages.map((error, index) => (
                                    <NotificationBanner key={index} notificationType="error">
                                        <div key={index}>{getUserFriendlyErrorMessage(error)}</div>
                                    </NotificationBanner>
                                ))}
                            </div>
                        )}
                        <div>
                            <BodyMedium className="mb-1.5rem">Contact Information</BodyMedium>
                            <AccountContactInformationFields
                                ageValidationError={ageValidationError}
                                disableName={!isNewMember}
                                hasBirthdate={!memberFromAPI?.prospect && memberWorking?.has_birthdate}
                                isPrimaryEmailRequired={isPrimaryEmailRequired}
                                setIsManualContactAddress={setIsManualContactAddress}
                                setAgeValidationError={setAgeValidationError}
                                isViewer={viewer.recordId === memberId}
                            />
                        </div>

                        <div className="mt-3.5rem mb-1.5rem">
                            <BodyMedium className="mb-1.5rem">Company Information</BodyMedium>
                            <CompanyFieldsProvider
                                viewerCompany={!!viewer?.company}
                                isNewMember={isNewMember}
                                disableCompanySelection={disableCompanySelection}
                                isEventRegistration={window.location.pathname.includes('event-registration')}
                                setTouched={setTouched}
                                touched={touched}
                                setFieldValue={setFieldValue}
                                isIndependent={initialFormValues.independent}
                                setIsNewCompany={setIsNewCompany}
                                setIsIndependent={setIsIndependent}
                            >
                                <AccountCompanyInformationFields
                                    setIsManualCompanyAddress={setIsManualCompanyAddress}
                                    setHasSecondaryBusinessTypes={setHasSecondaryBusinessTypes}
                                />
                            </CompanyFieldsProvider>
                        </div>

                        <GdprConsent
                            formValue={values.gdprConsent}
                            mainAddressCountry={values.mainAddressCountry}
                            name="gdprConsent"
                            setFieldValue={setFieldValue}
                            setIsRequired={setIsGdprConsentRequired}
                        />

                        <CaslConsent
                            formValue={values.caslExpressConsent}
                            mainAddressCountry={values.mainAddressCountry}
                            name="caslExpressConsent"
                            setFieldValue={setFieldValue}
                            setIsRequired={setIsCaslConsentRequired}
                        />

                        <ErrorFocus
                            ageValidationError={ageValidationError}
                            setAgeValidationError={setAgeValidationError}
                        />
                    </Form>
                );
            }}
        </Formik>
    );
};

AccountInformationForm.propTypes = {
    disableCompanySelection: PropTypes.bool,
    formRef: PropTypes.object,
    memberId: PropTypes.number,
    onMemberSaved: PropTypes.func,
    viewerId: PropTypes.number,
};

export default AccountInformationForm;
